import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Plus, TrendingDown, TrendingUp, Trash2 } from "lucide-react";

const CATS = ["raw_material", "utilities", "vendor_bill", "rent", "misc"];

export default function AdminExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [profit, setProfit] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/expenses").then(r => setExpenses(r.data));
    api.get("/expenses/profitability").then(r => setProfit(r.data));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      await api.post("/expenses", { ...editing, amount: Number(editing.amount) });
      toast.success("Expense logged"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/expenses/${id}`); load();
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Expenses · Profit</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">The real bottom line</h1>
          </div>
          <button data-testid="new-expense-btn" onClick={() => setEditing({ category: "raw_material", vendor_name: "", amount: 0, description: "" })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
            <Plus size={16} /> Log expense
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Revenue</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-emerald-700">{inr(profit?.revenue)}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">COGS</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-amber-700">{inr(profit?.cogs)}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Expenses</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-brand">{inr(profit?.expenses_total)}</div>
          </div>
          <div className="bg-ink text-white rounded-xl p-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-sun">Net profit</div>
            <div className="font-heading text-3xl font-extrabold mt-2">{inr(profit?.net_profit)}</div>
            <div className="text-xs text-white/60 mt-1">Net margin {profit?.net_margin_pct ?? 0}%</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Expenses by category</div>
            <div className="h-64 mt-3">
              {profit?.expenses_by_category?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={profit.expenses_by_category}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
                    <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#78716C" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716C" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }} />
                    <Bar dataKey="amount" fill="#D12D2D" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-ink-500">No expenses yet</div>}
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Margins</div>
            <div className="mt-4 space-y-3">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-600">Gross margin</span>
                  <span className="font-bold">{profit?.gross_margin_pct ?? 0}%</span>
                </div>
                <div className="mt-1 h-2 bg-cream-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.max(0, Math.min(100, profit?.gross_margin_pct || 0))}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-600">Net margin</span>
                  <span className="font-bold">{profit?.net_margin_pct ?? 0}%</span>
                </div>
                <div className="mt-1 h-2 bg-cream-200 rounded-full overflow-hidden">
                  <div className={`h-full ${(profit?.net_margin_pct||0)>=0?'bg-brand':'bg-red-700'}`} style={{ width: `${Math.max(0, Math.min(100, Math.abs(profit?.net_margin_pct || 0)))}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 bg-white rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 text-ink-700">
              <tr>
                <th className="p-3 text-left text-xs uppercase font-bold">Date</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Category</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Vendor</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Description</th>
                <th className="p-3 text-right text-xs uppercase font-bold">Amount</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id} className="border-t border-border" data-testid={`exp-row-${e.id}`}>
                  <td className="p-3">{e.date}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-cream-200 text-xs font-bold uppercase">{e.category}</span></td>
                  <td className="p-3">{e.vendor_name}</td>
                  <td className="p-3 text-ink-500">{e.description}</td>
                  <td className="p-3 text-right font-bold">{inr(e.amount)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => remove(e.id)} className="text-brand hover:bg-red-50 p-2 rounded"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">Log expense</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Category</label>
                <select data-testid="exp-cat" value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Amount (₹)</label>
                <input data-testid="exp-amount" type="number" value={editing.amount} onChange={e => setEditing({...editing, amount: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Vendor</label>
                <input data-testid="exp-vendor" value={editing.vendor_name} onChange={e => setEditing({...editing, vendor_name: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
                <input data-testid="exp-desc" value={editing.description} onChange={e => setEditing({...editing, description: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="exp-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
